import { useCallback, useMemo } from 'react';
import { useAtomValue, useAtom } from 'jotai';

import { Number3, SphericalImageProps, SvgMarkerSetProps } from '@fabric-web/3d-viewer';
import * as analysisState from '@/components/Analysis/state';
import * as poiState from '../state';
import {
  pointOfInterestIcon,
  pointOfInterestHoveredIcon,
} from '@/components/shared/PointOfInterestIcon';
import { PointOfInterestQuery } from '@/types';
import { uncertainBlisterColor } from '@/theme/colors';
import { upperSnakeCase } from '@/utils';
import { BLISTER_TYPE, PIT_TYPE } from '@/constants';
import { checkIfVoi } from '@/utils/poi';
import { PoiStatus } from '@/__generated__/graphql';
import { point3dToNumber3 } from '@/utils/pointOfInterest';
import * as state from '@/state';

const SCALE_ADJUSTMENT_FACTOR = 0.05;

export function usePointOfInterestMarkers(selectedSpherical?: SphericalImageProps) {
  const selectedAssemblyId = useAtomValue(analysisState.selectedAssemblyId);
  const poiFilteredByCheckbox = useAtomValue(poiState.poiFilteredByCheckbox);
  const [selectedPoiId, setSelectedPoiId] = useAtom(poiState.selectedPointOfInterestId);
  const [blisterToAdd, setBlisterToAdd] = useAtom(poiState.blisterToAdd);
  const editPointOfInterest = useAtomValue(poiState.editPointOfInterest);
  const arePoisVisible = useAtomValue(analysisState.arePoisVisible);
  const showOnlySelectedPoi = useAtomValue(analysisState.showOnlySelectedPoi);
  const poiTemplateIcons = useAtomValue(analysisState.poiTemplateIcons);
  const governingValueByAssemblyId = useAtomValue(analysisState.governingValueByAssemblyId);
  const allVoiTemplates = useAtomValue(analysisState.allVoiTemplates);
  const edlStrength = useAtomValue(state.edlStrength);
  const dynamicPointSize = useAtomValue(state.dynamicPointSize);
  const showPointOfInterestPointer = useAtomValue(analysisState.showPointOfInterestPointer);

  const allGoverningPois = useMemo(
    () =>
      new Set(
        Object.values(governingValueByAssemblyId)
          .map((value) => value.pointOfInterest?.id)
          .filter((value) => !!value)
      ),
    [governingValueByAssemblyId]
  );

  const scale = useMemo(() => {
    return SCALE_ADJUSTMENT_FACTOR * (selectedSpherical ? 0.3 : 0.5);
  }, [selectedSpherical]);

  const handleMarkerClick = useCallback(
    (poi: PointOfInterestQuery[0]) => {
      setSelectedPoiId(poi.id);
      if ([BLISTER_TYPE, PIT_TYPE, 'DEFECT_CLASS'].includes(poi.type))
        setBlisterToAdd((current) => ({
          ...current,
          state: 'Reviewing',
        }));
    },
    [setBlisterToAdd, setSelectedPoiId]
  );

  const poiToExclude = useMemo(() => {
    if (blisterToAdd?.state !== 'UpdatingBlister' && !editPointOfInterest.isAutoGenerated)
      return undefined;
    return editPointOfInterest.pointOfInterest?.id;
  }, [blisterToAdd, editPointOfInterest]);

  const filteredPois = useMemo(() => {
    if (!arePoisVisible) return [];

    const filteredPoisWithoutVois = poiFilteredByCheckbox.filter((poi) => {
      return !checkIfVoi(allVoiTemplates, poi.type);
    });

    if (showOnlySelectedPoi && selectedPoiId) {
      return filteredPoisWithoutVois.filter((poi) => poi.id === selectedPoiId);
    }

    if (selectedAssemblyId) {
      return filteredPoisWithoutVois.filter(
        (poi) => (poi.assembly?.id === selectedAssemblyId || poi.id === selectedPoiId) && poi.id !== poiToExclude
      );
    }
    return poiToExclude
      ? filteredPoisWithoutVois.filter((poi) => poi.id !== poiToExclude)
      : filteredPoisWithoutVois;
  }, [
    arePoisVisible,
    showOnlySelectedPoi,
    allVoiTemplates,
    poiFilteredByCheckbox,
    selectedAssemblyId,
    poiToExclude,
    selectedPoiId,
  ]);

  const poiMarkerSets: SvgMarkerSetProps[] = useMemo(() => {
    if (showPointOfInterestPointer) {
      return [];
    }

    const PointsOfInterestByType = new Map<
      string,
      {
        type: string;
        isUncertain: boolean;
        isGoverning: boolean;
        isVerified: boolean;
        isRemediated: boolean;
        isSelected: boolean;
        pois: PointOfInterestQuery;
      }
    >();
    for (const poi of filteredPois) {
      const isUncertain = poi.type === BLISTER_TYPE && !poi?.blister?.maxHeight;
      const isVerified = poi.isVerified ?? false;
      const isRemediated = poi.status === PoiStatus.Completed;
      const isGoverning = allGoverningPois.has(poi.id);
      const isSelected = selectedPoiId === poi.id;
      const key = `${poi.type}-${isUncertain}-${isGoverning}-${isVerified}-${isRemediated}-${isSelected}`;
      let entry = PointsOfInterestByType.get(key);
      if (!entry) {
        entry = {
          type: poi.type,
          isUncertain,
          isGoverning,
          isVerified,
          isRemediated,
          isSelected,
          pois: [],
        };
        PointsOfInterestByType.set(key, entry);
      }
      PointsOfInterestByType.set(key, {
        ...entry,
        pois: [...entry.pois, poi],
      });
    }
    const markerSets: SvgMarkerSetProps[] = [];
    PointsOfInterestByType.forEach(
      ({ type, isUncertain, isGoverning, isVerified, isRemediated, isSelected, pois }, index) => {
        const poiIcon =
          poiTemplateIcons.find((poiTemplate) => poiTemplate.name === upperSnakeCase(type))?.icon ??
          undefined;
        const poiIconColor =
          poiTemplateIcons.find((poiTemplate) => poiTemplate.name === upperSnakeCase(type))
            ?.color ?? undefined;

        const markerSet: SvgMarkerSetProps = {
          id: `poi-markers-${type.toLowerCase()}-${index}`,
          enableClippingByVisibilityBox: false,
          normalSvg: {
            url: pointOfInterestIcon({
              type,
              poiIcon: poiIcon || undefined,
              iconColor: poiIconColor || undefined,
              isUncertain,
              isGoverning,
              isVerified,
              isRemediated,
              isSelected,
              ...(isUncertain ? { overrideColor: uncertainBlisterColor } : {}),
            }),
            renderOrder: 2,
            zStep: 0.1,
            renderOrderStep: 0,
            depthWrite: true,
          },
          hoveredSvg: {
            url: pointOfInterestHoveredIcon({
              type: type,
              poiIcon: poiIcon || undefined,
              iconColor: poiIconColor || undefined,
              isGoverning,
              isSelected,
              isVerified,
              isRemediated,
              ...(isUncertain ? { overrideColor: uncertainBlisterColor } : {}),
            }),
            renderOrder: 2,
            zStep: 0.1,
            renderOrderStep: 0,
            depthWrite: true,
          },

          instances: pois
            .filter((poi) => !!poi.centerPoint3d)
            .map((poi) => ({
              id: poi.id,
              position: point3dToNumber3(poi.centerPoint3d) as Number3,
              normalScale: scale,
              hoveredScale: scale,
              onClick: () => handleMarkerClick(poi),
            })),
        };

        markerSets.push(markerSet);
      }
    );
    return markerSets;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredPois,
    poiTemplateIcons,
    selectedPoiId,
    scale,
    handleMarkerClick,
    allGoverningPois,
    edlStrength,
    dynamicPointSize,
    showPointOfInterestPointer,
  ]);

  return poiMarkerSets;
}
